import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Download } from "lucide-react";

const CATEGORIES = ["spirits", "beer", "wine", "mixer", "garnish", "other"];
const UNITS = ["bottle", "can", "keg", "case", "unit"];

const EMPTY = {
  name: "",
  sku: "",
  category: "spirits",
  unit: "bottle",
  current_stock: 0,
  par_level: 0,
  reorder_point: 0,
  unit_cost: 0,
  supplier_id: "",
};

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    const [i, s] = await Promise.all([
      api.get("/inventory"),
      api.get("/suppliers"),
    ]);
    setItems(i.data);
    setSuppliers(s.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const downloadTemplate = (kind = "standard") => {
    let rows;
    if (kind === "wine") {
      rows = [
        "producer,wine,vintage,on hand,par_level,reorder_point,price,supplier_name",
        "Domaine Leflaive,Puligny-Montrachet,2019,3,6,2,145.00,",
        "Caymus,Cabernet Sauvignon,2020,6,12,4,89.00,",
        "Veuve Clicquot,Yellow Label Brut,NV,4,8,2,52.00,",
      ];
    } else {
      rows = [
        "name,sku,category,unit,current_stock,par_level,reorder_point,unit_cost,supplier_name",
        "Bourbon Reserve,BR-001,spirits,bottle,10,20,8,32.50,",
        "IPA Draft Keg,IPA-K1,beer,keg,2,4,2,120.00,",
      ];
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = kind === "wine" ? "barkeep_wine_list_template.csv" : "barkeep_inventory_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/inventory/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { created, updated, skipped, errors } = r.data;
      const bits = [];
      if (created) bits.push(`${created} created`);
      if (updated) bits.push(`${updated} updated`);
      if (skipped) bits.push(`${skipped} skipped`);
      toast.success(`Imported: ${bits.join(", ") || "nothing"}`);
      if (errors?.length) {
        toast.error(`${errors.length} row error(s) — first: row ${errors[0].row} · ${errors[0].error}`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setImporting(false);
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({
      name: it.name,
      sku: it.sku || "",
      category: it.category,
      unit: it.unit,
      current_stock: it.current_stock,
      par_level: it.par_level,
      reorder_point: it.reorder_point,
      unit_cost: it.unit_cost,
      supplier_id: it.supplier_id || "",
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      current_stock: Number(form.current_stock),
      par_level: Number(form.par_level),
      reorder_point: Number(form.reorder_point),
      unit_cost: Number(form.unit_cost),
      supplier_id: form.supplier_id || null,
    };
    try {
      if (editing) {
        await api.put(`/inventory/${editing}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/inventory", payload);
        toast.success("Item added");
      }
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this item?")) return;
    try {
      await api.delete(`/inventory/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="inventory-root">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-500 font-mono-plex mb-2">
            Stockroom
          </div>
          <h1 className="font-display text-4xl font-bold">Inventory</h1>
          <p className="text-slate-500 text-sm mt-1">
            Track SKUs, par levels and reorder points.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => downloadTemplate("standard")}
            data-testid="csv-template-btn"
            className="rounded-md px-3 py-2 text-sm border border-white/10 text-slate-200 hover:bg-white/[0.03] flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> CSV template
          </button>
          <button
            onClick={() => downloadTemplate("wine")}
            data-testid="csv-wine-template-btn"
            className="rounded-md px-3 py-2 text-sm border border-white/10 text-slate-200 hover:bg-white/[0.03] flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Wine list template
          </button>
          <label
            className="rounded-md px-3 py-2 text-sm border border-white/10 text-slate-200 hover:bg-white/[0.03] flex items-center gap-2 cursor-pointer"
            data-testid="csv-import-btn"
          >
            <Upload className="w-4 h-4" />
            {importing ? "Importing…" : "Import CSV"}
            <input
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleImport}
              disabled={importing}
              data-testid="csv-file-input"
            />
          </label>
          <button onClick={openNew} className="btn-amber rounded-md px-4 py-2 text-sm flex items-center gap-2" data-testid="add-inventory-btn">
            <Plus className="w-4 h-4" /> Add item
          </button>
        </div>
      </div>

      <div className="surface-card rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-slate-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <div className="font-display text-2xl mb-2">No inventory yet</div>
            <p className="text-sm">Add your first bottle to get started.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 text-slate-500 text-xs uppercase tracking-widest">
                <th className="text-left px-6 py-3">Item</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Supplier</th>
                <th className="text-right px-4 py-3">Stock</th>
                <th className="text-right px-4 py-3">Par</th>
                <th className="text-right px-4 py-3">Reorder</th>
                <th className="text-right px-4 py-3">Cost</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const low = it.current_stock <= it.reorder_point;
                return (
                  <tr key={it.id} className="border-b border-white/5 hover:bg-white/[0.02] row-in" data-testid={`row-item-${it.id}`}>
                    <td className="px-6 py-3">
                      <div className="font-medium">{it.name}</div>
                      {it.sku && (
                        <div className="text-xs text-slate-500 font-mono-plex">
                          {it.sku}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400 capitalize">
                      {it.category}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {supplierName(it.supplier_id)}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono-plex ${low ? "text-red-400" : "text-emerald-400"}`}>
                      {it.current_stock}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-plex text-slate-400">
                      {it.par_level}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-plex text-slate-400">
                      {it.reorder_point}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-plex text-slate-400">
                      ${Number(it.unit_cost).toFixed(2)}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(it)}
                          className="p-1.5 hover:bg-white/[0.05] rounded"
                          data-testid={`edit-item-${it.id}`}
                        >
                          <Pencil className="w-4 h-4 text-slate-400" />
                        </button>
                        <button
                          onClick={() => del(it.id)}
                          className="p-1.5 hover:bg-red-500/10 rounded"
                          data-testid={`delete-item-${it.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
          <form
            onSubmit={save}
            className="w-full max-w-lg bg-[#12141a] border border-white/10 rounded-xl p-6 space-y-4"
            data-testid="inventory-form"
          >
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl font-bold">
                {editing ? "Edit item" : "Add item"}
              </div>
              <button type="button" onClick={() => setShowForm(false)} data-testid="close-form">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" required>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="inv-name" className={inputCls} />
              </Field>
              <Field label="SKU">
                <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className={inputCls} data-testid="inv-sku" />
              </Field>
              <Field label="Category">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="inv-category">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Unit">
                <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inputCls} data-testid="inv-unit">
                  {UNITS.map((u) => <option key={u}>{u}</option>)}
                </select>
              </Field>
              <Field label="Current stock">
                <input type="number" step="0.01" value={form.current_stock} onChange={(e) => setForm({ ...form, current_stock: e.target.value })} className={inputCls} data-testid="inv-stock" />
              </Field>
              <Field label="Par level">
                <input type="number" step="0.01" value={form.par_level} onChange={(e) => setForm({ ...form, par_level: e.target.value })} className={inputCls} data-testid="inv-par" />
              </Field>
              <Field label="Reorder point">
                <input type="number" step="0.01" value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} className={inputCls} data-testid="inv-reorder" />
              </Field>
              <Field label="Unit cost ($)">
                <input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} className={inputCls} data-testid="inv-cost" />
              </Field>
              <div className="col-span-2">
                <Field label="Supplier">
                  <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className={inputCls} data-testid="inv-supplier">
                    <option value="">— none —</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-md border border-white/10 text-sm hover:bg-white/[0.03]">
                Cancel
              </button>
              <button type="submit" className="btn-amber rounded-md px-4 py-2 text-sm" data-testid="save-inventory">
                {editing ? "Save changes" : "Add item"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-white text-sm outline-none focus:border-amber-500";

const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="text-xs text-slate-400 mb-1">
      {label} {required && <span className="text-amber-500">*</span>}
    </div>
    {children}
  </label>
);
